import { useState } from "react";
import { Link } from "react-router-dom";

interface Article {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  views: number;
  isNew?: boolean;
  isHot?: boolean;
}

interface ArticleGridProps {
  articles: Article[];
  pageSize?: number;
}

export default function ArticleGrid({ articles, pageSize = 12 }: ArticleGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(articles.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const displayedArticles = articles.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  if (articles.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
          <i className="ri-inbox-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-500">该分类下暂无文章</p>
      </div>
    );
  }

  return (
    <section className="px-6 md:px-10 py-10 md:py-14 bg-background-50">
      <div className="max-w-[1400px] mx-auto">
        {/* Sort bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序：</span>
            <div className="flex items-center bg-background-100 border border-background-200/70 rounded-full px-1 py-1">
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-50 text-foreground-950 cursor-pointer shadow-sm whitespace-nowrap"
              >
                最新发布
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-xs font-medium rounded-full text-foreground-500 hover:text-foreground-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                最多阅读
              </button>
            </div>
          </div>
          <span className="text-xs text-foreground-400">
            共 {articles.length} 篇
          </span>
        </div>

        {/* Article grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {displayedArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative w-full aspect-[16/10] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 黑料网`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 flex items-center gap-1.5">
                  {article.isNew && (
                    <span className="px-2 py-0.5 bg-accent-500 text-background-50 dark:text-foreground-950 text-[10px] font-bold rounded-md">
                      NEW
                    </span>
                  )}
                  {article.isHot && (
                    <span className="px-2 py-0.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-[10px] font-bold rounded-md">
                      HOT
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-semibold text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000
                      ? `${(article.views / 10000).toFixed(1)}万`
                      : article.views}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-950 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>

            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="w-9 h-9 flex items-center justify-center text-sm text-foreground-400"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page as number)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    currentPage === page
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-500 hover:text-foreground-950 hover:bg-background-100"
                  }`}
                >
                  {page}
                </button>
              )
            )}

            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-950 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}